// Pure helpers: turn the wizard's form state into one normalized object that
// all six templates consume, plus small shared utilities.

export const PAGE_WIDTH_PX = 794; // A4 width at 96dpi (210mm)

export const SKILL_LABELS = {
  languages: "Languages",
  frameworks: "Frameworks",
  cloudDevops: "Cloud / DevOps",
  databases: "Databases",
};

const clean = (v) => (v === null || v === undefined ? "" : String(v).trim());

// "2024-05" -> "May 2024". Parsed manually: new Date("2024-05") is UTC and can
// display as the previous month in timezones behind UTC.
export const formatMonth = (ym) => {
  if (!ym) return "";
  const [y, m] = String(ym).split("-").map(Number);
  if (!y || !m) return "";
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
};

export const toHref = (url) => {
  const u = clean(url);
  if (!u) return "";
  return /^(https?:|mailto:|tel:)/i.test(u) ? u : `https://${u}`;
};

export const buildFileName = (name) => {
  const base = clean(name)
    .replace(/[^\p{L}\p{N}]+/gu, "_")
    .replace(/^_+|_+$/g, "");
  return `${base || "My"}_Resume.pdf`;
};

export const buildResumeData = ({
  formData,
  experienceList,
  projectList,
  achievementList,
  isFresher,
}) => {
  const skills = Object.keys(SKILL_LABELS)
    .map((key) => ({
      key: SKILL_LABELS[key],
      value: clean(formData.skills?.[key]),
    }))
    .filter((s) => s.value);

  const eduSource = [
    [formData.pgDegree, formData.pgCollege, formData.pgYear, formData.pgScore],
    [
      formData.gradDegree,
      formData.gradCollege,
      formData.gradYear,
      formData.gradScore,
    ],
    [
      formData.interBoard,
      formData.interSchool,
      formData.interYear,
      formData.interScore,
    ],
  ];
  const education = eduSource
    .filter(([degree]) => clean(degree))
    .map(([degree, institute, year, score]) => ({
      degree: clean(degree),
      institute: clean(institute),
      year: clean(year),
      score: clean(score),
    }));

  const experiences = isFresher
    ? []
    : experienceList
        .filter((e) => clean(e.name) || clean(e.role))
        .map((e) => ({
          company: clean(e.name),
          role: clean(e.role),
          duration: [formatMonth(e.startDate), formatMonth(e.endDate)]
            .filter(Boolean)
            .join(" - "),
          description: clean(e.description),
        }));

  const projects = projectList
    .filter((p) => clean(p.projectName))
    .map((p) => ({
      projectName: clean(p.projectName),
      projectRole: clean(p.projectRole),
      technologiesUsed: clean(p.technology),
      projectLink: clean(p.projectLink),
      duration: clean(p.duration),
      description: (p.projectDescription || "")
        .split("\n")
        .map((line) => line.trim().replace(/^[-•*]\s*/, ""))
        .filter(Boolean),
    }));

  return {
    fullName: clean(formData.Name),
    position: clean(formData.position),
    email: clean(formData.email),
    phoneNo: clean(formData.phone),
    location: clean(formData.location),
    linkedinId: clean(formData.linkedin),
    githubId: clean(formData.github),
    summary: clean(formData.summary),
    skills,
    education,
    experiences,
    projects,
    others: achievementList.map((a) => clean(a.achievement)).filter(Boolean),
  };
};